<template>
  <router-link
    v-if="item.url"
    :to="item.url"
    custom
    v-slot="{ navigate, href }"
  >
    <a
      :href="href"
      class="adv-menu__item"
      :class="itemClasses"
      :style="itemStyle"
      :data-highlight="highlightString"
      :data-mode="dataMode"
      @click.prevent="handleClick"
    >
      <div
        v-if="hasCustomBg"
        class="adv-menu__item-bg"
        :style="itemBgStyle"
      />
      <span class="adv-menu__item-content">
        <Transition name="icon-fade" mode="out-in">
          <q-icon
            v-if="iconName"
            :key="item.id"
            :name="iconName"
            class="adv-menu__item-icon"
          />
        </Transition>
        <span v-if="item.label" class="adv-menu__item-label">{{ item.label }}</span>
      </span>
    </a>
  </router-link>
  <div
    v-else
    class="adv-menu__item"
    :class="itemClasses"
    :style="itemStyle"
    :data-highlight="highlightString"
    :data-mode="dataMode"
    @click="handleClick"
  >
    <div
      v-if="hasCustomBg"
      class="adv-menu__item-bg"
      :style="itemBgStyle"
    />
    <span class="adv-menu__item-content">
      <Transition name="icon-fade" mode="out-in">
        <q-icon
          v-if="iconName"
          :key="item.id"
          :name="iconName"
          class="adv-menu__item-icon"
        />
      </Transition>
      <span v-if="item.label" class="adv-menu__item-label">{{ item.label }}</span>
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, CSSProperties } from 'vue';
import type { ProcessedItem } from './composables/useProcessedItems';

export type ActiveBgHighlightEffect = "brightness" | "border" | "shadow" | "scale" | "bold";

interface Props {
  item: ProcessedItem;
  uniformStyle?: boolean;
  activeBackground?: string;
  activeColor?: string;
  activeFilledHighlight?: ActiveBgHighlightEffect[];
  activeOutlineHighlight?: ActiveBgHighlightEffect[];
  pauseAnimationOnActive?: boolean;
  enableTextColorTransition?: boolean;
  textColorGetter?: (id: string | number, color: string, isActive: boolean) => string;
}

const props = withDefaults(defineProps<Props>(), {
  uniformStyle: false,
  activeBackground: '#1976d2',
  activeColor: '#ffffff',
  activeFilledHighlight: () => ["brightness", "shadow", "scale", "bold"],
  activeOutlineHighlight: () => ["border", "shadow", "scale", "bold"],
  pauseAnimationOnActive: false,
  enableTextColorTransition: false,
});

const emit = defineEmits<{
  click: [];
}>();

const handleClick = (event: Event) => {
  // Emit regular click event
  emit('click');
};

// Icon name
const iconName = computed(() => {
  if (props.uniformStyle) return null;
  if (props.item.active && props.item.activeIcon) return props.item.activeIcon;
  return props.item.icon || null;
});

// Item mode
const itemMode = computed(() => {
  return props.item.mode || (props.item.bgColor ? 'filled' : 'flat');
});

// Has custom background
const hasCustomBg = computed(() => {
  return props.item.hasCustomBg;
});

// Highlight string for data attribute
const highlightString = computed(() => {
  if (!hasCustomBg.value) return undefined;

  if (itemMode.value === 'filled') {
    return props.activeFilledHighlight.join(' ');
  } else if (itemMode.value === 'outline') {
    return props.activeOutlineHighlight.join(' ');
  }

  return undefined;
});

// Data mode attribute
const dataMode = computed(() => {
  if (!hasCustomBg.value) return undefined;
  return itemMode.value;
});

// Item classes
const itemClasses = computed(() => ({
  "--active": props.item.active,
  "--has-bg": hasCustomBg.value,
  "--text-blink": props.item.textBlink,
  "--hidden": props.item.isHidden,
  [`--anim-${props.item.animation}`]: props.item.animation && hasCustomBg.value && !(props.pauseAnimationOnActive && props.item.active),
}));

// Background style
const itemBgStyle = computed((): CSSProperties => {
  const styles: CSSProperties = {};

  if (itemMode.value === 'filled') {
    styles.backgroundColor = props.item.bgColor || props.item.textColor || props.activeBackground;
  } else if (itemMode.value === 'outline') {
    const outlineColor = props.item.bgColor || props.item.textColor || props.activeBackground;
    styles.border = `2px solid ${outlineColor}`;
    styles.outlineOffset = '-2px';
    styles.backgroundColor = 'transparent';
  }

  styles.top = '4px';
  styles.bottom = '4px';

  return styles;
});

// Item style (text color)
const itemStyle = computed((): CSSProperties => {
  const styles: CSSProperties = {};

  if (props.uniformStyle) {
    if (props.enableTextColorTransition && props.textColorGetter) {
      styles.color = props.textColorGetter(
        props.item.id,
        props.item.active ? props.activeColor : 'inherit',
        props.item.active
      );
    } else if (props.item.active) {
      styles.color = props.activeColor;
    }
    return styles;
  }

  if (hasCustomBg.value) {
    if (itemMode.value === 'outline') {
      if (props.item.active) {
        const textColor = "#ffffff";
        if (props.enableTextColorTransition && props.textColorGetter) {
          styles.color = props.textColorGetter(props.item.id, textColor, props.item.active);
        } else {
          styles.color = textColor;
        }
      } else {
        const textColor = props.item.bgColor || props.item.textColor || props.activeBackground;
        if (props.enableTextColorTransition && props.textColorGetter) {
          styles.color = props.textColorGetter(props.item.id, textColor, props.item.active);
        } else {
          styles.color = textColor;
        }
      }
    } else {
      if (props.enableTextColorTransition && props.textColorGetter) {
        styles.color = props.textColorGetter(
          props.item.id,
          props.item.textColor || "#ffffff",
          props.item.active
        );
      } else {
        styles.color = props.item.textColor || "#ffffff";
      }
    }
  } else if (props.item.active) {
    styles.color = props.activeColor;
  } else if (props.item.textColor) {
    if (props.enableTextColorTransition && props.textColorGetter) {
      styles.color = props.textColorGetter(props.item.id, props.item.textColor, false);
    } else {
      styles.color = props.item.textColor;
    }
  }

  return styles;
});
</script>

<style lang="scss" scoped>
.adv-menu__item {
  position: relative;
  display: inline-flex;
  align-items: center;
  padding: 8px 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  white-space: nowrap;
  transition: color 0.4s ease-out 0.1s, opacity 0.3s ease, max-width 0.3s ease, padding 0.3s ease, margin 0.3s ease;
  flex-shrink: 0;
  max-width: 500px;
  overflow: hidden;
  contain: layout paint;

  @media (min-width: $breakpoint-md-max) {
    &:hover:not(.--active):not(.--has-bg) {
      background-color: rgba(0, 0, 0, 0.05);
    }
  }


  &.--has-bg {
    transition: filter 0.2s ease, color 0.4s ease-out 0.1s, opacity 0.3s ease, max-width 0.3s ease, padding 0.3s ease, margin 0.3s ease;

    &:hover {
      filter: brightness(0.95);
    }

    &[data-mode="filled"] {
      .adv-menu__item-icon,
      .adv-menu__item-label {
        color: #FFFFFF;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      }
    }
  }

  &.--hidden {
    opacity: 0;
    max-width: 0;
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: calc(var(--menu-gap, 10px) * -0.5) !important;
    margin-right: calc(var(--menu-gap, 10px) * -0.5) !important;
    pointer-events: none;
  }
}

.adv-menu__item-bg {
  position: absolute;
  top: 2px;
  bottom: 2px;
  left: 0;
  right: 0;
  border-radius: 8px;
  z-index: -1;
  isolation: isolate;
  backface-visibility: hidden;
  transition: all 0.2s ease;
  opacity: 0.75;
  box-sizing: border-box;
}

.adv-menu__item[data-mode="outline"] .adv-menu__item-bg {
  top: 2px;
  bottom: 2px;
}

.adv-menu__item.--active.--has-bg {
  &[data-mode="filled"] {
    .adv-menu__item-icon,
    .adv-menu__item-label {
      text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    }
  }

  &[data-highlight~="brightness"] .adv-menu__item-bg {
    opacity: 1;
  }

  &[data-highlight~="border"] .adv-menu__item-bg {
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.5);
  }

  &[data-highlight~="shadow"] .adv-menu__item-bg {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
  }

  &[data-highlight~="border"][data-highlight~="shadow"] .adv-menu__item-bg {
    box-shadow: inset 0 0 0 2px rgba(255, 255, 255, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.35);
  }

  &[data-highlight~="scale"] .adv-menu__item-bg {
    transform: scale(1.05);
    transition: transform 0.2s cubic-bezier(0.25, 0.1, 0.25, 1);
  }

  &[data-highlight~="bold"] .adv-menu__item-label {
    font-weight: 600;
  }
}

.adv-menu__item-content {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.adv-menu__item-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
  transition: color 0.4s ease-out 0.1s, opacity 0.3s ease-out;
  will-change: color, opacity;
}

.adv-menu__item-label {
  color: #2f2f2f;
  line-height: 1.2;
  transition: color 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.1s;
  will-change: color;
}

.adv-menu__item.--active:not(.--has-bg) {
  .adv-menu__item-label,
  .adv-menu__item-icon {
    color: #ffffff;
  }
}

.adv-menu__item.--active.--has-bg[data-mode="outline"] {
  .adv-menu__item-label,
  .adv-menu__item-icon {
    color: #ffffff;
  }
}

@keyframes textBlink {
  0% {
    text-shadow: 0 0 1px #fff,
    0 0 1px #fff,
    0 0 2px #fff,
    0 0 2px #fff,
    0 0 2px #fff,
    0 0 2px #fff,
    0 0 2px #fff,
    0 0 2px #fff,
    0 0 10px #fff,
    0 0 10px #fff,
    0 0 10px currentColor
  }
}

.adv-menu__item.--text-blink {
  .adv-menu__item-label {
    color: #fff;
    animation: textBlink 2s ease-out infinite;
    text-shadow: 0 0 1px #fff, 0 0 2px #fff;
    text-transform: uppercase;
  }
}

@keyframes bgGradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.adv-menu__item.--anim-gradient .adv-menu__item-bg {
  background: linear-gradient(
          270deg,
          var(--anim-color-1, currentColor),
          var(--anim-color-2, color-mix(in srgb, currentColor 60%, white)),
          var(--anim-color-3, color-mix(in srgb, currentColor 90%, black)),
          var(--anim-color-1, currentColor)
  );
  background-size: 300% 300%;
  animation: bgGradient 4s ease infinite;
}

@keyframes bgPulse {
  0%, 100% {
    opacity: 0.6;
    transform: scale(0.99);
  }
  50% {
    opacity: 1;
    transform: scale(1.03);
  }
}

.adv-menu__item.--anim-pulse .adv-menu__item-bg {
  animation: bgPulse 2s ease-in-out infinite;
}

@keyframes bgShimmer {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

.adv-menu__item.--anim-shimmer .adv-menu__item-bg {
  background: linear-gradient(
          90deg,
          transparent 0%,
          transparent 40%,
          rgba(255, 255, 255, 0.4) 50%,
          transparent 60%,
          transparent 100%
  ),
  currentColor;
  background-size: 200% 100%, 100% 100%;
  background-position: -200% center, center;
  animation: bgShimmer 3s linear infinite;
}

@keyframes bgBreathe {
  0%, 100% {
    filter: brightness(1) saturate(1);
    box-shadow: inset 0 0 0 0 rgba(255, 255, 255, 0);
  }
  50% {
    filter: brightness(1.2) saturate(1.3);
    box-shadow: inset 0 0 20px 0 rgba(255, 255, 255, 0.3);
  }
}

.adv-menu__item.--anim-breathe .adv-menu__item-bg {
  animation: bgBreathe 3s ease-in-out infinite;
}

@keyframes bgGlow {
  0%, 100% {
    box-shadow: inset 0 0 10px rgba(255, 255, 255, 0.2),
    0 0 5px currentColor;
  }
  50% {
    box-shadow: inset 0 0 20px rgba(255, 255, 255, 0.4),
    0 0 15px currentColor,
    0 0 25px currentColor;
  }
}

.adv-menu__item.--anim-glow .adv-menu__item-bg {
  animation: bgGlow 2s ease-in-out infinite;
}

.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: opacity 0.15s ease-out;
}

.icon-fade-enter-from {
  opacity: 0;
}

.icon-fade-leave-to {
  opacity: 0;
}
</style>
